import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useContext,
} from 'react'
import injectStyles from 'react-jss'
import { Col, Row } from 'react-bootstrap'
import { useJsApiLoader } from '@react-google-maps/api'

import Box from '@material-ui/core/Box'
import SubmitButton from 'Components/Form/SubmitButton'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import Typography from '@material-ui/core/Typography'

import { SectionTitle } from 'Components/Section/SectionTitle'
import MapWithToolbar from 'Components/MapWithToolbar'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { Checkbox } from 'Components/Form/Checkbox'
import { SnackBarContext } from 'Components/SnackBar'

import { CONFIG } from 'Config/config'

import { updateSelfServeCitySettings } from '../../Containers/Country/gql'
import { getCitySettingsData } from 'Containers/Form/City/CityAddEdit/EditCityView/gql.js'
import {
  getDistributionZones,
  updateDistributionZones,
} from '../../Module/graphQL/PathFinder/pathFinderGQL'
import { searchAndSaveIntersectedPolygonsHandler } from './utils'
import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'

import styles from './styles'
import { getFormatedArea } from '../../Containers/BuilderMap/util'
import useOverlaysFetch from '../../Utils/Hooks/useOverlaysFetch'
import { debounce } from '../../Utils/Functions'

const libraries = ['geometry', 'places', 'drawing']

const MAP_TYPES = {
  CIRCLE: 'circle',
  POLYGON: 'polygon',
}

const ZONE_TYPES = [
  { value: 'h2h', label: 'H2H Zone' },
  { value: 'd2d', label: 'D2D Zone' },
]

const VIEW_EDIT_MODE = { VIEW: 'view', EDIT: 'edit' }

const AREA_TYPES = [
  { value: 'distribution', label: 'Distribution' },
  { value: 'exclusion', label: 'Exclusion' },
]

const processOverlays = overlays =>
  overlays.map(overlay => ({
    ...overlay,
    paths: overlay.paths[0],
    type: 'd2d_polygon',
  }))

const DistributionZones = ({ classes, cityId, country }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: CONFIG.GOOGLE_API_KEY,
    libraries: libraries,
    language: 'en',
  })

  let refCircle = useRef([])
  let refPolygon = useRef([])

  const [zoneType, setZoneType] = useState(ZONE_TYPES[0])
  const [areaType, setAreaType] = useState(AREA_TYPES[0])
  const [saving, setSaving] = useState(false)
  const [savedPercentage, setSavedPercentage] = useState(1)

  const [prevDistributionZones, setPrevDistributionZones] = useState({
    h2h: [],
    d2d: [],
  })
  const [distributionZones, setDistributionZones] = useState({
    h2h: [],
    d2d: [],
  })
  const [undoRedoState, setUndoRedoState] = useState({
    h2h: { undo: [], redo: [] },
    d2d: { undo: [], redo: [] },
  })
  const [showHideAreas, setShowHideAreas] = useState(true)
  const [viewEditMode, setViewEditMode] = useState(VIEW_EDIT_MODE.VIEW)

  const [showH2HZones, setShowH2HZones] = useState(true)
  const [showD2DZones, setShowD2DZones] = useState(true)
  const [importedArea, setImportedArea] = useState([])

  const [importAreaValue, setImportAreaValue] = useState({
    h2h: [''],
    d2d: [''],
  })
  const [cityLocation, setCityLocation] = useState({})
  const [cityName, setCityName] = useState('')
  const [map, setMap] = useState(null)
  const [isLoadingPolygons, setIsLoadingPolygons] = useState(false)
  const [polygonsInfo, setPolygonsInfo] = useState({ totalCount: 0, loaded: 0 })
  const [polygons, setPolygons] = useState({})
  const [isShowPolygons, setIsShowPolygons] = useState(false)
  const isUS = useMemo(() => !!country && country.code === 'US', [country])
  const runSnackBar = useContext(SnackBarContext)
  let requestId = null

  const boundsChangedListenerRef = useRef(null)

  const cityResponse = useGraphQLFetch({
    fetchFn: getCitySettingsData,
    defaultFetchArgs: cityId,
    fetchId: 'getCitySettingsData',
  })

  const distributionZonesResponse = useGraphQLFetch({
    fetchFn: getDistributionZones,
    defaultFetchArgs: cityId,
    fetchId: 'getDistributionZones',
  })

  const { loadData } = useOverlaysFetch({
    countryCode: country.code,
    processOverlays,
  })

  const fetchMissionBoundaries = async ({
    layer = 'MissionBoundaries',
    area,
    type,
    previousBoundaries = {},
    includeAddressCount = false,
    after,
    cityId,
  }) => {
    const { data, pageInfo, totalCount } = await loadData({
      layer,
      area,
      includeAddressCount,
      after,
      cityId,
    })

    if (!data) {
      return {}
    }

    if (pageInfo.hasNextPage) {
      const boundaries = { ...previousBoundaries, ...data }

      setPolygonsInfo({ totalCount, loaded: Object.keys(boundaries).length })

      return await fetchMissionBoundaries({
        layer,
        after: pageInfo.endCursor,
        type,
        area,
        previousBoundaries: boundaries,
        cityId,
      })
    } else {
      const boundaries = { ...previousBoundaries, ...data }

      setPolygonsInfo({ totalCount, loaded: Object.keys(boundaries).length })

      return boundaries
    }
  }

  const getPolygons = async map => {
    if (!isShowPolygons) {
      return
    }
    try {
      const latLngBound = map.getBounds()

      if (!latLngBound) return
      const area = await getFormatedArea(latLngBound)

      setIsLoadingPolygons(true)
      const currentRequestId = Date.now()
      requestId = currentRequestId

      const polygons = await fetchMissionBoundaries({
        area,
        layer: isUS ? 'USCarrierRoutes' : 'MissionBoundaries',
      })

      if (currentRequestId !== requestId) {
        //Race condition occurred
        return
      }
      setPolygons(polygons)
    } catch (error) {
      console.error('PathFinder error.')
      throw error
    } finally {
      setIsLoadingPolygons(false)
      setPolygonsInfo({ totalCount: 0, loaded: 0 })
    }
  }

  const onMapInit = newMap => {
    if (!newMap || map) return
    setMap(newMap)
  }

  useEffect(() => {
    if (map) {
      if (isShowPolygons) {
        // Add the listener and store the reference
        boundsChangedListenerRef.current = map.addListener(
          'bounds_changed',
          debounce(() => getPolygons(map), 1000),
        )

        // Perform initial polygons fetching
        getPolygons(map)
      } else {
        // Remove the listener using the correct method
        if (boundsChangedListenerRef.current) {
          google.maps.event.removeListener(boundsChangedListenerRef.current)
          boundsChangedListenerRef.current = null // Reset the ref
        }
      }
    }
  }, [isShowPolygons, map])

  useEffect(() => {
    if (cityResponse.isLoaded) {
      const cityInfo = cityResponse.data.city
      if (cityInfo) {
        setCityLocation({
          lat: cityInfo.location.latitude,
          lng: cityInfo.location.longitude,
        })
        setCityName(cityInfo.name)
      }
    }
  }, [cityResponse.isLoaded])

  useEffect(() => {
    if (distributionZonesResponse.isLoaded) {
      const { data, errorMessage } = distributionZonesResponse

      if (!data) {
        return runSnackBar({
          type: 'error',
          msg: `${errorMessage}`,
        })
      }

      const alreadyImportedAreas = Object.entries(
        data.distributionZones,
      ).reduce((acc, [type, zones]) => {
        return {
          ...acc,
          [type]: [
            ...[''],
            ...zones.reduce(
              (accumulator, zone) =>
                zone.imported ? accumulator.add(zone.areaType) : accumulator,
              new Set([]),
            ),
          ],
        }
      }, {})

      setImportAreaValue(alreadyImportedAreas)
      setPrevDistributionZones(data.distributionZones)
      setDistributionZones(data.distributionZones)
    }
  }, [distributionZonesResponse.isLoaded])

  const handleDistributionZonesUpdate = async () => {
    setSaving(true)
    try {
      const { h2h, d2d } = distributionZones

      // if one of the channels does not have distribution zones
      // disable the channel in the city settings
      if (
        !d2d.some(zone => zone.areaType === 'distribution') ||
        !h2h.some(zone => zone.areaType === 'distribution')
      ) {
        const updateSettingsPayload = {
          id: cityId,
        }
        if (!d2d.some(zone => zone.areaType === 'distribution')) {
          updateSettingsPayload.enabledChannelLetterbox = false
        }

        if (!h2h.some(zone => zone.areaType === 'distribution')) {
          updateSettingsPayload.enabledChannelPreciseLocations = false
        }

        if (
          'enabledChannelLetterbox' in updateSettingsPayload ||
          'enabledChannelPreciseLocations' in updateSettingsPayload
        ) {
          await updateSelfServeCitySettings(updateSettingsPayload)
        }
      }
      const response = await updateDistributionZones({
        cityId,
        name: cityName,
        h2h: h2h,
        d2d: d2d,
        countryCode: country.code,
        countryName: country.name,
      })

      const {
        data: {
          updateDistributionZones: { success },
        },
      } = await response.json()

      if (success && isUS) {
        await searchAndSaveIntersectedPolygonsHandler({
          cityId,
          cityName,
          collectionName: isUS ? 'USCarrierRoutes' : 'MissionBoundaries',
          d2dDistributionZones: d2d,
          setSavedPercentage,
        })
      }

      if (success) {
        runSnackBar({
          type: 'success',
          msg: `${cityName} distribution zones saved successfully`,
        })
        setPrevDistributionZones(distributionZones)
        setSavedPercentage(1)
      } else {
        runSnackBar({
          type: 'error',
          msg: `There is an error while saving ${cityName} distribution zones`,
        })
      }
    } catch (e) {
      runSnackBar({
        type: 'error',
        msg: `There is an error while saving ${cityName} distribution zones`,
      })
    }
    setSaving(false)
  }

  const removeEditModeFromDZ = () => {
    setDistributionZones({
      h2h: distributionZones.h2h.map(distributionZone => ({
        ...distributionZone,
        editMode: false,
      })),
      d2d: distributionZones.d2d.map(distributionZone => ({
        ...distributionZone,
        editMode: false,
      })),
    })
  }

  const handleShowHideAreasClick = () => {
    removeEditModeFromDZ()
    setShowHideAreas(!showHideAreas)
  }

  const handleViewEditModeClick = mode => {
    setShowHideAreas(true)
    removeEditModeFromDZ()
    setViewEditMode(mode)
  }

  const getImportAreaOptions = useCallback(() => {
    switch (zoneType.value) {
      case 'h2h':
        if (distributionZones.d2d.length) {
          const isDistributionZonesExists = distributionZones.d2d.find(
            mapItem => mapItem.areaType === 'distribution' && !mapItem.imported,
          )
          const isExclusionZonesExists = distributionZones.d2d.find(
            mapItem => mapItem.areaType === 'exclusion' && !mapItem.imported,
          )
          const importAreaOptions = []
          if (isDistributionZonesExists) {
            importAreaOptions.push({
              value: 'distribution',
              label: 'D2D Distribution Zone',
            })
          }
          if (isExclusionZonesExists) {
            importAreaOptions.push({
              value: 'exclusion',
              label: 'D2D Exclusion Areas',
            })
          }
          return importAreaOptions
        } else return []
      case 'd2d':
        if (distributionZones.h2h.length) {
          const isDistributionZonesExists = distributionZones.h2h.find(
            mapItem => mapItem.areaType === 'distribution' && !mapItem.imported,
          )
          const isExclusionZonesExists = distributionZones.h2h.find(
            mapItem => mapItem.areaType === 'exclusion' && !mapItem.imported,
          )
          const importAreaOptions = []
          if (isDistributionZonesExists) {
            importAreaOptions.push({
              value: 'distribution',
              label: 'H2H Distribution Zone',
            })
          }
          if (isExclusionZonesExists) {
            importAreaOptions.push({
              value: 'exclusion',
              label: 'H2H Exclusion Areas',
            })
          }
          return importAreaOptions
        } else return []

      default:
        return []
    }
  }, [zoneType, distributionZones])

  const importAreaOptions = useMemo(() => getImportAreaOptions(), [
    zoneType,
    distributionZones,
  ])

  const handleImportAreasChange = (...args) => {
    const value = args[2]

    setImportedArea(value)
  }

  useEffect(() => {
    setImportAreaValue({
      ...importAreaValue,
      [zoneType.value]: importedArea.length ? importedArea : [''],
    })
  }, [importedArea])

  useEffect(() => {
    if (!importAreaOptions.length) return

    if (zoneType.value === 'h2h') {
      const newDZ = distributionZones[zoneType.value].filter(
        distributionZone => !distributionZone.imported,
      )
      setDistributionZones({
        ...distributionZones,
        [zoneType.value]: [
          ...newDZ,
          ...distributionZones.d2d.reduce(
            (acc, distributionZone) =>
              importAreaValue.h2h.includes(distributionZone.areaType) &&
              !distributionZone.imported
                ? [
                    ...acc,
                    {
                      ...distributionZone,
                      imported: true,
                      id: `${+new Date() + Math.random()}`,
                    },
                  ]
                : acc,
            [],
          ),
        ],
      })
    } else {
      const newDZ = distributionZones[zoneType.value].filter(
        distributionZone => !distributionZone.imported,
      )
      setDistributionZones({
        ...distributionZones,
        [zoneType.value]: [
          ...newDZ,
          ...distributionZones.h2h.reduce((acc, distributionZone) => {
            return importAreaValue.d2d.includes(distributionZone.areaType) &&
              !distributionZone.imported
              ? [
                  ...acc,
                  {
                    ...distributionZone,
                    imported: true,
                    id: `${+new Date() + Math.random()}`,
                  },
                ]
              : acc
          }, []),
        ],
      })
    }
  }, [importAreaValue])

  const zonesAvailability = useMemo(() => {
    return [...distributionZones.h2h, ...distributionZones.d2d].reduce(
      (acc, item) => {
        if (item.areaType === 'distribution') {
          return { ...acc, hasDistributionZones: true }
        } else return { ...acc, hasExclusionZones: true }
      },
      {
        hasDistributionZones: false,
        hasExclusionZones: false,
      },
    )
  }, [distributionZones])

  const handleUndo = useCallback(() => {
    if (!undoRedoState[zoneType.value].undo.length) return

    setDistributionZones({
      ...distributionZones,
      [zoneType.value]:
        undoRedoState[zoneType.value].undo.length > 1
          ? undoRedoState[zoneType.value].undo[
              undoRedoState[zoneType.value].undo.length - 1
            ]
          : undoRedoState[zoneType.value].undo[0],
    })
    setUndoRedoState({
      ...undoRedoState,
      [zoneType.value]: {
        undo:
          undoRedoState[zoneType.value].undo.length > 1
            ? undoRedoState[zoneType.value].undo.slice(0, -1)
            : [],
        redo: undoRedoState[zoneType.value].redo.length
          ? [
              ...undoRedoState[zoneType.value].redo,
              distributionZones[zoneType.value],
            ]
          : [distributionZones[zoneType.value]],
      },
    })
  }, [undoRedoState[zoneType.value], distributionZones])

  const handleRedo = useCallback(() => {
    if (!undoRedoState[zoneType.value].redo.length) return
    setDistributionZones({
      ...distributionZones,
      [zoneType.value]:
        undoRedoState[zoneType.value].redo.length > 1
          ? undoRedoState[zoneType.value].redo[
              undoRedoState[zoneType.value].redo.length - 1
            ]
          : undoRedoState[zoneType.value].redo[0],
    })
    setUndoRedoState({
      ...undoRedoState,
      [zoneType.value]: {
        undo: undoRedoState[zoneType.value].undo.length
          ? [
              ...undoRedoState[zoneType.value].undo,
              distributionZones[zoneType.value],
            ]
          : [distributionZones[zoneType.value]],
        redo:
          undoRedoState[zoneType.value].redo.length > 1
            ? undoRedoState[zoneType.value].redo.slice(0, -1)
            : [],
      },
    })
  }, [undoRedoState[zoneType.value], distributionZones])

  const handleDelete = useCallback(() => {
    const selectedMapItem = distributionZones[zoneType.value].filter(
      distributionZone => distributionZone.editMode,
    )
    if (!selectedMapItem.length) return

    const newDZ = {
      ...distributionZones,
      [zoneType.value]: distributionZones[zoneType.value].filter(
        distributionZone => !distributionZone.editMode,
      ),
    }
    setDistributionZones(newDZ)

    const newUndoRedoState = {
      ...undoRedoState,
      [zoneType.value]: {
        ...undoRedoState[zoneType.value],
        undo: undoRedoState[zoneType.value].undo.length
          ? [
              ...undoRedoState[zoneType.value].undo,
              distributionZones[zoneType.value],
            ]
          : [distributionZones[zoneType.value]],
      },
    }

    setUndoRedoState(newUndoRedoState)
  }, [distributionZones])

  const removeEditMode = useCallback(() => {
    const newDZ = {
      ...distributionZones,
      [zoneType.value]: distributionZones[zoneType.value].map(
        distributionZone => ({ ...distributionZone, editMode: false }),
      ),
    }
    setDistributionZones(newDZ)
  }, [distributionZones])

  const handleMapItemClick = item => {
    const newDZ = {
      ...distributionZones,
      [zoneType.value]: distributionZones[zoneType.value].map(
        distributionZone => ({
          ...distributionZone,
          editMode: item.id === distributionZone.id ? true : false,
        }),
      ),
    }

    setDistributionZones(newDZ)
  }

  const onCircleComplete = circle => {
    const { center, radius } = circle

    const newMarkerObj = {
      id: `${+new Date() + Math.random()}`,
      value: `New circle ${distributionZones[zoneType.value].length + 1}`,
      center: {
        lat: center.lat(),
        lng: center.lng(),
      },
      radius: radius,
      type: MAP_TYPES.CIRCLE,
      areaType: areaType.value,
      editMode: true,
    }

    const newDZ = {
      ...distributionZones,
      [zoneType.value]: [...distributionZones[zoneType.value], newMarkerObj],
    }

    setDistributionZones(newDZ)

    circle.setMap(null)

    const newUndoRedoState = {
      ...undoRedoState,
      [zoneType.value]: {
        ...undoRedoState[zoneType.value],
        undo: undoRedoState[zoneType.value].undo.length
          ? [
              ...undoRedoState[zoneType.value].undo,
              distributionZones[zoneType.value],
            ]
          : [distributionZones[zoneType.value]],
        redo: [],
      },
    }

    setUndoRedoState(newUndoRedoState)
  }

  const onPolygonComplete = polygon => {
    if (!polygon) return

    if (polygon.getPath().getArray().length < 3) {
      polygon.setPath([])
      return
    }

    const newMarkerObj = {
      id: `${+new Date() + Math.random()}`,
      value: `New polygon ${distributionZones[zoneType.value].length + 1}`,
      paths: polygon.getPath().getArray(),
      type: MAP_TYPES.POLYGON,
      areaType: areaType.value,
      editMode: true,
    }

    const newDZ = {
      ...distributionZones,
      [zoneType.value]: [...distributionZones[zoneType.value], newMarkerObj],
    }

    setDistributionZones(newDZ)

    polygon.setMap(null)

    const newUndoRedoState = {
      ...undoRedoState,
      [zoneType.value]: {
        ...undoRedoState[zoneType.value],
        undo: undoRedoState[zoneType.value].undo.length
          ? [
              ...undoRedoState[zoneType.value].undo,
              distributionZones[zoneType.value],
            ]
          : [distributionZones[zoneType.value]],
        redo: [],
      },
    }

    setUndoRedoState(newUndoRedoState)
  }

  const handlePolygonResize = polygon => {
    if (refPolygon.current && refPolygon.current[polygon.id]) {
      const newPolygon = refPolygon.current[polygon.id].state.polygon
      const newPaths = newPolygon.getPath().getArray()

      if (JSON.stringify(newPaths) === JSON.stringify(polygon.paths)) return

      const newDZ = {
        ...distributionZones,
        [zoneType.value]: distributionZones[zoneType.value].map(
          distributionZone =>
            distributionZone.id === polygon.id
              ? {
                  ...polygon,
                  paths: newPaths,
                }
              : distributionZone,
        ),
      }

      const newUndoRedoState = {
        ...undoRedoState,
        [zoneType.value]: {
          ...undoRedoState[zoneType.value],
          undo: undoRedoState[zoneType.value].undo.length
            ? [
                ...undoRedoState[zoneType.value].undo,
                distributionZones[zoneType.value],
              ]
            : [distributionZones[zoneType.value]],
        },
      }

      setDistributionZones(newDZ)
      setUndoRedoState(newUndoRedoState)
    }
  }

  const handleCircleCenterChange = circle => {
    if (refCircle.current && refCircle.current[circle.id]) {
      const newCircle = refCircle.current[circle.id].state.circle
      const newDZ = {
        ...distributionZones,
        [zoneType.value]: distributionZones[zoneType.value].map(
          distributionZone =>
            distributionZone.id === circle.id
              ? {
                  ...circle,
                  center: {
                    lat: newCircle.center.lat(),
                    lng: newCircle.center.lng(),
                  },
                }
              : distributionZone,
        ),
      }

      const newUndoRedoState = {
        ...undoRedoState,
        [zoneType.value]: {
          ...undoRedoState[zoneType.value],
          undo: undoRedoState[zoneType.value].undo.length
            ? [
                ...undoRedoState[zoneType.value].undo,
                distributionZones[zoneType.value],
              ]
            : [distributionZones[zoneType.value]],
        },
      }

      setDistributionZones(newDZ)
      setUndoRedoState(newUndoRedoState)
    }
  }

  const handleCircleRadiusChange = circle => {
    if (refCircle.current && refCircle.current[circle.id]) {
      const newCircle = refCircle.current[circle.id].state.circle

      const newDZ = {
        ...distributionZones,
        [zoneType.value]: distributionZones[zoneType.value].map(
          distributionZone =>
            distributionZone.id === circle.id
              ? {
                  ...circle,
                  radius: newCircle.radius,
                }
              : distributionZone,
        ),
      }

      const newUndoRedoState = {
        ...undoRedoState,
        [zoneType.value]: {
          ...undoRedoState[zoneType.value],
          undo: undoRedoState[zoneType.value].undo.length
            ? [
                ...undoRedoState[zoneType.value].undo,
                distributionZones[zoneType.value],
              ]
            : [distributionZones[zoneType.value]],
        },
      }

      setDistributionZones(newDZ)
      setUndoRedoState(newUndoRedoState)
    }
  }

  const mapItems = useMemo(() => {
    return viewEditMode === VIEW_EDIT_MODE.VIEW
      ? [
          ...(showH2HZones ? distributionZones.h2h : []),
          ...(showD2DZones ? distributionZones.d2d : []),
          ...(isShowPolygons ? Object.values(polygons) : []),
        ]
      : [
          ...distributionZones[zoneType.value],
          ...(isShowPolygons ? Object.values(polygons) : []),
        ]
  }, [
    distributionZones,
    showH2HZones,
    showD2DZones,
    zoneType,
    undoRedoState,
    polygons,
    isShowPolygons,
  ])

  return isLoaded ? (
    <Box>
      <Row>
        <Col xs={6} className={classes.sectionTitle}>
          <SectionTitle>Distribution Zones</SectionTitle>
        </Col>
        <Col xs={6} className={classes.sectionTitle}>
          <Box display="flex" justifyContent="flex-end">
            <ButtonGroup disableElevation color="primary">
              <SubmitButton
                value="View"
                variant={
                  viewEditMode === VIEW_EDIT_MODE.VIEW
                    ? 'contained'
                    : 'outlined'
                }
                onClick={() => handleViewEditModeClick(VIEW_EDIT_MODE.VIEW)}
              />
              <SubmitButton
                value="Edit"
                variant={
                  viewEditMode === VIEW_EDIT_MODE.EDIT
                    ? 'contained'
                    : 'outlined'
                }
                onClick={() => handleViewEditModeClick(VIEW_EDIT_MODE.EDIT)}
              />
            </ButtonGroup>
          </Box>
        </Col>
      </Row>
      {viewEditMode === VIEW_EDIT_MODE.VIEW ? (
        <Box>
          {distributionZones.h2h.length > 0 ||
          distributionZones.d2d.length > 0 ? (
            <Row className={classes.selectionsRow}>
              {distributionZones.h2h.length ? (
                <Col xs={2}>
                  <Checkbox
                    label={'H2H Zone'}
                    shouldListenToInputValue
                    input={{
                      value: showH2HZones,
                      onChange: () => setShowH2HZones(!showH2HZones),
                    }}
                  />
                </Col>
              ) : null}
              {distributionZones.d2d.length ? (
                <Col xs={2}>
                  <Checkbox
                    label={'D2D Zone'}
                    shouldListenToInputValue
                    input={{
                      value: showD2DZones,
                      onChange: () => setShowD2DZones(!showD2DZones),
                    }}
                  />
                </Col>
              ) : null}
            </Row>
          ) : (
            <Typography className={classes.noDistibutionZones}>
              No distribution zones defined
            </Typography>
          )}
        </Box>
      ) : (
        <Row className={classes.selectionsRow}>
          <Col xs={3} className={classes.sectionTitle}>
            <SelectDashboard
              label="Zone Type"
              options={ZONE_TYPES}
              onChange={(event, index) => setZoneType(ZONE_TYPES[index])}
              input={{
                value: zoneType.value,
              }}
            />
          </Col>
          <Col xs={3} className={classes.sectionTitle}>
            <SelectDashboard
              label="Area Edit Type"
              options={AREA_TYPES}
              onChange={(event, index) => setAreaType(AREA_TYPES[index])}
              input={{
                value: areaType.value,
              }}
            />
          </Col>
          <Col xs={3} className={classes.sectionTitle}>
            <SelectDashboard
              label="Import Area"
              options={
                importAreaOptions.length
                  ? importAreaOptions
                  : [{ value: '', label: 'Empty' }]
              }
              onChange={handleImportAreasChange}
              shouldListenToInputValue
              input={{
                value: importAreaValue[zoneType.value],
                disabled: !importAreaOptions.length,
                multiple: true,
              }}
              style={{
                display: 'none',
              }}
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col xs={12}>
          <MapWithToolbar
            onMapInit={onMapInit}
            saving={saving}
            savedPercentage={savedPercentage}
            label={zoneType.label}
            showToolBar={viewEditMode === VIEW_EDIT_MODE.EDIT}
            circleOptions={item => ({
              fillColor:
                item.areaType === 'distribution'
                  ? 'rgba(78, 171, 150, 0.3)'
                  : 'rgba(238, 67, 96, 0.3)',
              fillOpacity:
                (!showHideAreas &&
                  viewEditMode === VIEW_EDIT_MODE.EDIT &&
                  item.areaType === 'distribution') ||
                (viewEditMode === VIEW_EDIT_MODE.VIEW &&
                  item.areaType === 'distribution')
                  ? 0
                  : 1,
              clickable: viewEditMode === VIEW_EDIT_MODE.EDIT,
              draggable: false,
              editable:
                viewEditMode === VIEW_EDIT_MODE.EDIT ? item.editMode : false,
              visible: !(
                !showHideAreas &&
                viewEditMode === VIEW_EDIT_MODE.VIEW &&
                item.areaType === 'exclusion'
              ),
            })}
            polygonOptions={item => ({
              fillColor:
                item.areaType === 'distribution'
                  ? 'rgba(78, 171, 150, 0.3)'
                  : 'rgba(238, 67, 96, 0.3)',
              fillOpacity:
                (!showHideAreas &&
                  viewEditMode === VIEW_EDIT_MODE.EDIT &&
                  item.areaType === 'distribution') ||
                (viewEditMode === VIEW_EDIT_MODE.VIEW &&
                  item.areaType === 'distribution')
                  ? 0
                  : 1,
              clickable: viewEditMode === VIEW_EDIT_MODE.EDIT,

              editable:
                viewEditMode === VIEW_EDIT_MODE.EDIT ? item.editMode : false,
              visible: !(
                !showHideAreas &&
                viewEditMode === VIEW_EDIT_MODE.VIEW &&
                item.areaType === 'exclusion'
              ),
            })}
            activeUndo={undoRedoState[zoneType.value].undo.length}
            activeRedo={undoRedoState[zoneType.value].redo.length}
            mapItemFillColor={
              areaType.value === 'distribution'
                ? 'rgba(78, 171, 150, 0.3)'
                : 'rgba(238, 67, 96, 0.3)'
            }
            mapItems={mapItems}
            center={cityLocation}
            refCircle={refCircle}
            refPolygon={refPolygon}
            handleUndo={handleUndo}
            handleRedo={handleRedo}
            handleDelete={handleDelete}
            removeEditMode={removeEditMode}
            handleMapItemClick={handleMapItemClick}
            onCircleComplete={onCircleComplete}
            onPolygonComplete={onPolygonComplete}
            handleCircleCenterChange={handleCircleCenterChange}
            handleCircleRadiusChange={handleCircleRadiusChange}
            handlePolygonResize={handlePolygonResize}
            loading={isLoadingPolygons}
            polygonsInfo={polygonsInfo}
          />
        </Col>
      </Row>
      <Row className={classes.showHideAreas}>
        {(viewEditMode === VIEW_EDIT_MODE.EDIT &&
          zonesAvailability.hasDistributionZones) ||
        (viewEditMode === VIEW_EDIT_MODE.VIEW &&
          zonesAvailability.hasExclusionZones) ? (
          <Col xs={2}>
            <Checkbox
              label={
                viewEditMode === VIEW_EDIT_MODE.EDIT
                  ? 'Distribution Zone Fill'
                  : 'Exclusion Areas'
              }
              shouldListenToInputValue
              input={{
                value: showHideAreas,
                onChange: handleShowHideAreasClick,
              }}
            />
          </Col>
        ) : null}
        <Col xs={2}>
          <Checkbox
            label={'Show D2D Polygons'}
            shouldListenToInputValue
            input={{
              value: isShowPolygons,
              onChange: () => setIsShowPolygons(!isShowPolygons),
              disabled: isLoadingPolygons,
            }}
          />
        </Col>
      </Row>
      <Row className={classes.saveBtnWrapper}>
        <Col xs={12}>
          <SubmitButton
            onClick={() => handleDistributionZonesUpdate()}
            value={'Save'}
            disabled={
              JSON.stringify(prevDistributionZones) ===
                JSON.stringify(distributionZones) || saving
            }
          />
        </Col>
      </Row>
    </Box>
  ) : (
    <div>Loading...</div>
  )
}

export default injectStyles(styles)(DistributionZones)
